import log from 'loglevel'

class EnvParseError extends Error {}

export interface Envs {
  env: string

  apiBaseUrl: string
  mobileAuthApiBaseUrl: string

  sentryDsn: string
  sentrySampleRate: number
  sentryReplaySessionSampleRate: number

  mobileAuthGoogleClientId: string
  mobileAuthProjectId: string
  mobileAuthProviderId: string
  mobileAuthUseInCountries: string[]
  mobileAuthUseForVersionsRe: RegExp
  mobileAuthUseInterstitialUxFlow: boolean

  firebaseRecaptchaEnabled: boolean

  debugConsoleEnabled: boolean
  debugViewEnabled: boolean
  debugSkipWalletCheckHeader: boolean

  deviceApiWebClientName: string
  deviceApiWebClientVersion: string
  deviceApiWebClientLocalization: string

  installOfALink: string
  installMiniLink: string

  testingApiKeyOfA: string
  testingApiKeyMini: string

  loggingEnabled: boolean

  logoutReportingEnabled: boolean

  debugInfoSecretHash: string

  ambassadorsUrl: string

  clientSdkJsApiWaitTimeMs: number

  rewardsOnlyOnSdk: boolean
}

interface EnvObj {
  [key: string]: unknown
}

function ensureString<IObj, IKey extends keyof IObj & string>(
  obj: IObj,
  key: IKey,
  normalize?: (val: string) => string
): string {
  const val = obj[key]

  if (val === undefined || val === null) {
    throw new EnvParseError(`${key} is missing`)
  }

  if (typeof val === 'string') {
    return normalize ? normalize(val) : val
  }

  throw new EnvParseError(`${key}="${val}" is not a string`)
}

function ensureStringArray<IObj, IKey extends keyof IObj & string>(
  obj: IObj,
  key: IKey,
  normalize?: (val: string) => string
): string[] {
  const val = obj[key]

  if (val === undefined || val === null) {
    throw new EnvParseError(`${key} is missing`)
  }

  if (!(typeof val === 'string')) {
    throw new EnvParseError(`${key}="${val}" is not a string`)
  }

  const raw = (val ? val.split(',') : []).map((v) => v.trim())
  return normalize ? raw.map(normalize) : raw
}

function ensureNumber<IObj, IKey extends keyof IObj & string>(obj: IObj, key: IKey): number {
  const val = obj[key]
  if (val === undefined || val === null) {
    throw new EnvParseError(`${key} is missing`)
  }

  const parsed = Number(val)

  if (!Number.isNaN(parsed)) {
    return parsed
  }

  throw new EnvParseError(`${key}="${val}" is not a number`)
}

function ensureBoolean<IObj, IKey extends keyof IObj & string>(obj: IObj, key: IKey): boolean {
  const val = obj[key]
  if (val === undefined || val === null) {
    throw new EnvParseError(`${key} is missing`)
  }

  if (val === 'true') {
    return true
  }

  if (val === 'false') {
    return false
  }

  throw new EnvParseError(`${key}="${val}" is not a boolean`)
}

function ensureRegExp<IObj, IKey extends keyof IObj & string>(obj: IObj, key: IKey): RegExp {
  const val = obj[key]
  if (val === undefined || val === null) {
    throw new EnvParseError(`${key} is missing`)
  }

  if (!(typeof val === 'string')) {
    throw new EnvParseError(`${key}="${val}" is not a string`)
  }

  if (val === '') return /(?!)/

  try {
    return new RegExp(val)
  } catch (e) {
    throw new EnvParseError(`${key}="${val}" is not a valid regular expression`)
  }
}

let envsCache: Envs | 'ENVS_VARS_NOT_SET' = 'ENVS_VARS_NOT_SET'

function getEnvs(useCache: boolean, envObj: EnvObj): Envs {
  if (useCache && envsCache !== 'ENVS_VARS_NOT_SET') {
    return envsCache
  }

  const envVars: Envs = {
    env: ensureString(envObj, 'VITE_ENV'),
    apiBaseUrl: ensureString(envObj, 'VITE_API_BASE_URL'),
    mobileAuthApiBaseUrl: ensureString(envObj, 'VITE_MOBILE_AUTH_API_BASE_URL'),

    sentryDsn: ensureString(envObj, 'VITE_SENTRY_DSN'),
    sentrySampleRate: ensureNumber(envObj, 'VITE_SENTRY_SAMPLE_RATE'),
    sentryReplaySessionSampleRate: ensureNumber(envObj, 'VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE'),

    mobileAuthGoogleClientId: ensureString(envObj, 'VITE_MOBILE_AUTH_CLIENT_ID'),
    mobileAuthProjectId: ensureString(envObj, 'VITE_MOBILE_AUTH_PROJECT_ID'),
    mobileAuthProviderId: ensureString(envObj, 'VITE_MOBILE_AUTH_PROVIDER_ID'),
    mobileAuthUseInCountries: ensureStringArray(
      envObj,
      'VITE_MOBILE_AUTH_USE_IN_COUNTRIES',
      (code) => code.toUpperCase()
    ),
    mobileAuthUseForVersionsRe: ensureRegExp(envObj, `VITE_MOBILE_AUTH_USE_FOR_VERSIONS_RE`),
    mobileAuthUseInterstitialUxFlow: ensureBoolean(
      envObj,
      `VITE_MOBILE_AUTH_USE_INTERSTITIAL_UX_FLOW`
    ),

    firebaseRecaptchaEnabled: ensureBoolean(envObj, 'VITE_FIREBASE_RECAPTCHA_ENABLED'),

    debugConsoleEnabled: ensureBoolean(envObj, 'VITE_DEBUG_CONSOLE_ENABLED'),
    debugViewEnabled: ensureBoolean(envObj, 'VITE_DEBUG_VIEW_ENABLED'),
    debugSkipWalletCheckHeader: ensureBoolean(envObj, 'VITE_DEBUG_SKIP_WALLET_CHECK_HEADER'),

    deviceApiWebClientName: ensureString(envObj, 'VITE_CLIENT_API_WEB_CLIENT_NAME'),
    deviceApiWebClientVersion: ensureString(envObj, 'VITE_CLIENT_API_WEB_CLIENT_VERSION'),
    deviceApiWebClientLocalization: ensureString(
      envObj,
      'VITE_CLIENT_API_WEB_CLIENT_LOCALIZATION',
      (code) => code.toUpperCase()
    ),

    installOfALink: ensureString(envObj, 'VITE_INSTALL_OFA_LINK'),
    installMiniLink: ensureString(envObj, 'VITE_INSTALL_MINI_LINK'),

    testingApiKeyMini: ensureString(envObj, 'VITE_TESTING_APIKEY_MINI'),
    testingApiKeyOfA: ensureString(envObj, 'VITE_TESTING_APIKEY_OFA'),

    loggingEnabled: ensureBoolean(envObj, 'VITE_ENV_LOGGING_ENABLED'),

    logoutReportingEnabled: ensureBoolean(envObj, 'VITE_LOGOUT_REPORTING_ENABLED'),

    debugInfoSecretHash: ensureString(envObj, 'VITE_DEBUG_INFO_SECRET_HASH'),

    ambassadorsUrl: ensureString(envObj, 'VITE_AMBASSADORS_URL'),

    // Number of milliseconds the page will wait for the Client SDK JS API
    // to load.
    //
    // In contrast to JS API exposed by Mini/OfA within WebView, client sdk does
    // not guarantee that its JS API will be available immediately on page load,
    // but requires this frontend to wait for it to be loaded.
    clientSdkJsApiWaitTimeMs: ensureNumber(envObj, 'VITE_CLIENT_SDK_JS_API_WAIT_TIME_MS'),

    rewardsOnlyOnSdk: ensureBoolean(envObj, 'VITE_REWARDS_ONLY_ON_SDK')
  }

  envsCache = envVars

  return envVars
}

/**
 * Env overrides allows to override given env values at runtime via local
 * storage.
 *
 * This is useful for debugging, testing, or local development.
 *
 * Example:
 * If you want to override VITE_API_BASE_URL then you must run the following:
 *
 * localStorage.setItem(
 *   'omm-envOverrides',
 *   JSON.stringify({ "VITE_API_BASE_URL": "https://api.example.com" })
 * )
 */
function getEnvLocalStorageOverrides(): EnvObj {
  const envOverrideRaw = localStorage.getItem('omm-envOverrides')
  if (envOverrideRaw === null) return {}

  try {
    const value = JSON.parse(envOverrideRaw) as unknown
    if (typeof value !== 'object' || value === null) {
      log.debug('Ignoring env overrides as they have unexpected value')
      return {}
    }

    return { ...value }
  } catch (error: unknown) {
    log.debug('Ignoring env overrides as parse resulted in error', error)
    return {}
  }
}

/**
 * Returns environment variable value injected at build time.
 *
 * For overrides @see getEnvOverridesFromWindow or ADR:
 * doc/adr/0007-env-variables-runtime-overrides.md.
 */
export function getEnv<T extends keyof Envs>(
  envName: T,
  useCache = true,
  envObj: EnvObj = import.meta.env
): Envs[T] {
  const envs = getEnvAll(useCache, envObj)
  return envs[envName]
}

export function getEnvAll(useCache = true, envObj: EnvObj = import.meta.env): Envs {
  return getEnvs(useCache, { ...envObj, ...getEnvLocalStorageOverrides() })
}
